import type { PortalConfigInput, ResolvedPortalConfig } from '../types/portal-config';

/**
 * Creates a fully resolved PortalConfig from the provided input.
 * Pre-computes lookup structures so hooks stay fast at runtime.
 *
 * @param input - App-specific portal configuration
 * @returns Resolved config consumed by PortalCoreProvider
 *
 * @example
 * ```typescript
 * // src/config/portal.config.ts
 * import { createPortalConfig } from '@lucid/portal-core';
 * import { LuppApi } from '../api/services/lupp/lupp.service';
 *
 * export const portalConfig = createPortalConfig({
 *   defaultLocale: 'de-de',
 *   defaultLanguage: 'en',
 *   rolesNamespace: `https://${import.meta.env.VITE_AUTH0_DOMAIN}/roles`,
 *   fetchIdentity: () => LuppApi.get('sales/v1/partners/me'),
 *   crossAppPrefixes: [],
 *   locales: [
 *     { value: 'de-de', label: 'Deutschland - Deutsch', group: 'Europe' },
 *     { value: 'en-gb', label: 'United Kingdom - English', group: 'Europe' },
 *   ],
 *   pages: [
 *     { id: 'prospectDesk', navId: 'prospect-desk' },
 *     { id: 'orderHub',     navId: 'order-hub' },
 *     { id: 'vehicleHub',   navId: 'vehicle-hub', enabled: false },
 *   ],
 * });
 * ```
 */
export const createPortalConfig = (input: PortalConfigInput): ResolvedPortalConfig => {
  const pages = input.pages ?? [];
  const locales = input.locales ?? [];

  const localeValues = new Set(locales.map((l) => l.value.toLowerCase()));

  const navIdToPageId: Record<string, string> = {};
  for (const page of pages) {
    navIdToPageId[page.navId] = page.id;
  }

  return {
    defaultLocale: input.defaultLocale,
    defaultLanguage: input.defaultLanguage ?? null,
    rolesNamespace: input.rolesNamespace,
    fetchIdentity: input.fetchIdentity,
    crossAppPrefixes: input.crossAppPrefixes ?? [],
    locales,
    pages,
    localeValues,
    navIdToPageId,
    filterLocalesByRole: input.filterLocalesByRole,
  };
};
